import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Minus, Plus, Search, UserMinus } from 'lucide-react';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { NativeSelect } from '../../../ui/select-native';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../ui/table';
import { cn } from '../../../../lib/utils';
import {
  formatPoints,
  isVoteCategory,
  parsePoints,
  rankLeaderboard,
  withCommunityVotes,
} from '../../../../lib/stages';
import {
  addStageParticipants,
  removeStageParticipants,
  setLeaderboardValue,
} from '../../../../database/tournamentStages';
import { handleFirebaseError } from '../../../../utils/errorHandler';
import { entryByline } from '../../../../lib/entries';
import ConfirmDialog from '../../common/ConfirmDialog';

// Wait this long after the last − / + click before saving, so a burst of
// clicks is one write.
const STEP_SAVE_DELAY = 500;

const toText = (value) => (value === null || value === undefined ? '' : String(value));

// One score: type a number, or use − / + (or the arrow keys). Saves by itself.
// Empty means "no score yet"; clearing a cell removes the score.
const PointsCell = ({ value, label, row, col, onSave, onNext }) => {
  const [text, setText] = useState(() => toText(value));
  const [invalid, setInvalid] = useState(false);
  const [saving, setSaving] = useState(false);
  const focused = useRef(false);
  const pending = useRef(null);
  const timer = useRef(null);
  const lastSent = useRef(undefined);
  const saveRef = useRef(onSave);
  saveRef.current = onSave;

  // Skips values that are already stored or already on their way, so Enter
  // followed by the blur it causes is one write.
  const save = async (next) => {
    if (next === (value ?? null) || next === lastSent.current) return;
    lastSent.current = next;
    setSaving(true);
    try {
      await saveRef.current(next);
    } catch (err) {
      toast.error('Could not save the points.', { description: handleFirebaseError(err) });
    } finally {
      setSaving(false);
    }
  };

  // Show updates from elsewhere unless the admin is busy with this cell.
  useEffect(() => {
    lastSent.current = undefined;
    if (focused.current || pending.current !== null) return;
    setText(toText(value));
    setInvalid(false);
  }, [value]);

  // Don't drop a step that is still waiting to be saved.
  useEffect(
    () => () => {
      clearTimeout(timer.current);
      if (pending.current !== null) saveRef.current(pending.current).catch(() => {});
    },
    []
  );

  const flush = () => {
    clearTimeout(timer.current);
    const next = pending.current;
    pending.current = null;
    if (next !== null) save(next);
  };

  const step = (delta) => {
    const current = parsePoints(text) ?? 0;
    const next = Math.max(0, Math.round((current + delta) * 100) / 100);
    setText(String(next));
    setInvalid(false);
    pending.current = next;
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, STEP_SAVE_DELAY);
  };

  // Returns false when the text isn't a valid score, so Enter stays put.
  const commit = () => {
    const trimmed = text.trim();
    if (!trimmed) {
      clearTimeout(timer.current);
      pending.current = null;
      setInvalid(false);
      save(null);
      return true;
    }
    const parsed = parsePoints(trimmed);
    if (parsed === null) {
      setInvalid(true);
      toast.error('Points must be a number of 0 or more.');
      return false;
    }
    setInvalid(false);
    pending.current = parsed;
    flush();
    return true;
  };

  return (
    <div
      className={cn(
        'ml-auto inline-flex h-9 items-stretch overflow-hidden rounded-md border border-input bg-background transition-opacity focus-within:ring-2 focus-within:ring-ring',
        invalid && 'border-destructive',
        saving && 'opacity-60'
      )}
    >
      <button
        type="button"
        tabIndex={-1}
        onClick={() => step(-1)}
        disabled={(parsePoints(text) ?? 0) <= 0}
        aria-label={`1 point less for ${label}`}
        className="flex w-8 items-center justify-center text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
      >
        <Minus className="h-3.5 w-3.5" />
      </button>
      <input
        data-row={row}
        data-col={col}
        value={text}
        inputMode="decimal"
        placeholder="–"
        aria-label={`Points for ${label}`}
        aria-invalid={invalid || undefined}
        onFocus={(e) => {
          focused.current = true;
          e.target.select();
        }}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          focused.current = false;
          commit();
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            step(e.key === 'ArrowUp' ? 1 : -1);
          } else if (e.key === 'Enter') {
            e.preventDefault();
            if (commit()) onNext();
          }
        }}
        className="w-14 border-x border-input bg-transparent text-center font-mono text-sm tabular-nums outline-none placeholder:text-muted-foreground/50"
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => step(1)}
        aria-label={`1 point more for ${label}`}
        className="flex w-8 items-center justify-center text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

const LeaderboardResultsEditor = ({ tournamentId, stage, index, players, playersById, votes }) => {
  const [search, setSearch] = useState('');
  const [addId, setAddId] = useState('');
  const [adding, setAdding] = useState(false);
  const [withdraw, setWithdraw] = useState(null);
  const [withdrawing, setWithdrawing] = useState(false);
  // Table when every score column fits the space we have, else one card per
  // entry (phones, or many categories next to the admin sidebar).
  const boxRef = useRef(null);
  const [boxWidth, setBoxWidth] = useState(null);
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const measure = () => setBoxWidth(el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const wide = boxWidth === null || boxWidth >= 300 + stage.categories.length * 140;

  const participants = stage.participants || [];
  const missing = players.filter((p) => !participants.includes(p.id));

  const ranked = votes ? withCommunityVotes(stage, votes) : stage;
  const rowById = Object.fromEntries(rankLeaderboard(ranked).map((r) => [r.playerId, r]));

  // Rows keep their seed order while editing, so nothing jumps under the
  // cursor; the # column shows the live rank.
  const filtered = participants.filter((id) =>
    (playersById[id]?.name || '').toLowerCase().includes(search.trim().toLowerCase())
  );

  const handleAdd = async () => {
    if (!addId) return;
    setAdding(true);
    try {
      await addStageParticipants(tournamentId, stage.id, [addId]);
      setAddId('');
    } catch (err) {
      toast.error('Could not add the player.', { description: handleFirebaseError(err) });
    } finally {
      setAdding(false);
    }
  };

  const confirmWithdraw = async () => {
    setWithdrawing(true);
    try {
      await removeStageParticipants(tournamentId, stage, [withdraw.id]);
      toast.success(`${withdraw.name} taken out of ${stage.name}.`);
      setWithdraw(null);
    } catch (err) {
      toast.error('Could not take them out.', { description: handleFirebaseError(err) });
    } finally {
      setWithdrawing(false);
    }
  };

  const nameOf = (id) => playersById[id]?.name || 'Removed player';

  const scoreCell = (playerId, c, row, col) => {
    const name = nameOf(playerId);
    if (isVoteCategory(c)) {
      const v = votes?.[playerId];
      return (
        <span className="font-mono tabular-nums">
          {v?.count ? (
            <>
              {v.average.toFixed(1)} <span className="text-muted-foreground">({v.count})</span>
            </>
          ) : (
            '—'
          )}
        </span>
      );
    }
    return (
      <PointsCell
        value={stage.results?.[playerId]?.[c.id] ?? null}
        label={stage.categories.length > 1 ? `${name}, ${c.name}` : name}
        row={row}
        col={col}
        onSave={(value) => setLeaderboardValue(tournamentId, stage.id, playerId, c.id, value)}
        onNext={() => focusCell(row + 1, col)}
      />
    );
  };

  const withdrawButton = (playerId) => (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
      aria-label={`Take ${nameOf(playerId)} out of ${stage.name}`}
      title={`Take out of ${stage.name}`}
      onClick={() => setWithdraw({ id: playerId, name: nameOf(playerId) })}
    >
      <UserMinus className="h-4 w-4" />
    </Button>
  );

  const nameBlock = (playerId) => {
    const byline = entryByline(playersById[playerId]);
    return (
      <div className="min-w-0">
        <div className="truncate font-medium">{nameOf(playerId)}</div>
        {byline && <div className="truncate text-xs text-muted-foreground">by {byline}</div>}
      </div>
    );
  };

  const focusCell = (row, col) => {
    const el = document.querySelector(`[data-row="${row}"][data-col="${col}"]`);
    if (el) el.focus();
  };

  return (
    <div ref={boxRef} className="space-y-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search entries"
            className="pl-9"
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Type points or use − and +. Enter jumps to the next entry. Every change saves by itself.
        </p>
      </div>

      {index === 0 && missing.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <NativeSelect
            value={addId}
            onChange={(e) => setAddId(e.target.value)}
            className="max-w-xs"
          >
            <option value="">Add a late entry from the roster…</option>
            {missing.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </NativeSelect>
          <Button type="button" size="sm" disabled={!addId || adding} onClick={handleAdd}>
            {adding ? 'Adding…' : 'Add'}
          </Button>
        </div>
      )}

      {!wide ? (
        <ul className="space-y-3">
          {filtered.map((playerId, row) => {
            const rankedRow = rowById[playerId];
            return (
              <li key={playerId} className="rounded-md border border-border/60 p-3">
                <div className="flex items-center gap-3">
                  <span className="w-6 shrink-0 text-right font-mono text-sm tabular-nums text-muted-foreground">
                    {rankedRow?.rank ?? '—'}
                  </span>
                  <div className="min-w-0 flex-1">{nameBlock(playerId)}</div>
                  {stage.categories.length > 1 && (
                    <span className="font-mono text-sm font-semibold tabular-nums">
                      {!rankedRow || rankedRow.rank === null ? '—' : formatPoints(rankedRow.total)}
                    </span>
                  )}
                  {withdrawButton(playerId)}
                </div>
                <div className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
                  {stage.categories.map((c, col) => (
                    <div key={c.id} className="flex items-center justify-between gap-3 text-sm">
                      <span className="min-w-0 truncate text-muted-foreground">{c.name}</span>
                      {scoreCell(playerId, c, row, col)}
                    </div>
                  ))}
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12 text-right">#</TableHead>
              <TableHead>Entry</TableHead>
              {stage.categories.map((c) => (
                <TableHead key={c.id} className="text-right">
                  {c.name}
                </TableHead>
              ))}
              {stage.categories.length > 1 && <TableHead className="text-right">Total</TableHead>}
              <TableHead className="w-10">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((playerId, row) => {
              const rankedRow = rowById[playerId];
              return (
                <TableRow key={playerId}>
                  <TableCell className="text-right font-mono tabular-nums text-muted-foreground">
                    {rankedRow?.rank ?? '—'}
                  </TableCell>
                  <TableCell className="min-w-[10rem] max-w-[16rem]">
                    {nameBlock(playerId)}
                  </TableCell>
                  {stage.categories.map((c, col) => (
                    <TableCell key={c.id} className="text-right">
                      {scoreCell(playerId, c, row, col)}
                    </TableCell>
                  ))}
                  {stage.categories.length > 1 && (
                    <TableCell className="text-right font-mono font-semibold tabular-nums">
                      {!rankedRow || rankedRow.rank === null ? '—' : formatPoints(rankedRow.total)}
                    </TableCell>
                  )}
                  <TableCell className="text-right">{withdrawButton(playerId)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <ConfirmDialog
        open={!!withdraw}
        title={`Take ${withdraw?.name} out of ${stage.name}?`}
        description="Their scores in this stage are deleted and they disappear from its leaderboard and community vote. They stay in the roster, so you can add them back as a late entry."
        confirmLabel="Take out"
        destructive
        busy={withdrawing}
        onOpenChange={(o) => !o && setWithdraw(null)}
        onConfirm={confirmWithdraw}
      />
    </div>
  );
};

export default LeaderboardResultsEditor;
